// ThemeCard: displays a single weekly theme with status badge, finding, impact, and related players.
// Related providers link into the Provider detail page so the reader can drill from theme → provider.
// Source examples render as clickable links when they contain a URL.

import React, { useState } from 'react';
import { t, statusLabel } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import type { Theme } from '../lib/api';
import { VerifiedBadge } from './VerifiedBadge';

interface ThemeCardProps {
  theme: Theme;
  lang: Lang;
  code?: string;
  // When false, week-over-week framing like 'artıyor / azalıyor' is not
  // meaningful (there is no prior week to compare with) and must be hidden.
  hasPriorWeek?: boolean;
}

function StatusBadge({ status, lang }: { status: Theme['status']; lang: Lang }) {
  const classes = {
    CRITICAL: 'badge-critical',
    WATCH: 'badge-watch',
    STABLE: 'badge-stable',
  };
  return <span className={classes[status]}>{statusLabel(status, lang)}</span>;
}

// Pull the first URL out of an arbitrary source-example string. Sonar returns
// these as either bare URLs, "Title — https://..." or just a title — handle all.
function extractUrl(s: string): string | null {
  const m = s.match(/https?:\/\/[^\s)\]]+/);
  return m ? m[0] : null;
}

function stripUrl(s: string, url: string): string {
  return s.replace(url, '').replace(/[—·:\-]\s*$/, '').replace(/^\s*[—·:\-]\s*/, '').trim();
}

export function ThemeCard({ theme, lang, code, hasPriorWeek = false }: ThemeCardProps) {
  const [expanded, setExpanded] = useState(false);

  const providerHref = (id: string) => {
    const codePart = code ? `?code=${encodeURIComponent(code)}` : '';
    return `/providers/${encodeURIComponent(id)}${codePart}`;
  };

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-sm font-medium text-neutral-900 leading-snug flex-1">{theme.title}</h3>
        <div className="flex items-center gap-2 shrink-0">
          {/* Trend label only renders when a prior week exists to compare against.
              On the first cycle every trend value would be invented, so we hide it. */}
          {hasPriorWeek && theme.trend && (
            <span className="text-xs text-neutral-500">{theme.trend}</span>
          )}
          <StatusBadge status={theme.status} lang={lang} />
        </div>
      </div>

      <p className="text-xs text-neutral-600 mb-3 leading-relaxed">{theme.finding}</p>

      {theme.impact && (
        <p className="text-xs text-neutral-500 mb-3 italic">{t('impact', lang)}: {theme.impact}</p>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-4 text-xs text-neutral-500 mb-3 flex-wrap">
        <span>{t('mentions', lang)}: <span className="text-neutral-700">{theme.mentionCount}</span></span>
        {theme.relatedPlayers.length > 0 && (
          <span>
            {t('relatedProviders', lang)}:{' '}
            {theme.relatedPlayers.map((p, i) => (
              <span key={p.id}>
                {i > 0 && ', '}
                <a
                  href={providerHref(p.id)}
                  className={`underline decoration-dotted decoration-neutral-700 hover:text-emerald-600 hover:decoration-emerald-500 transition-colors ${p.unverified ? 'text-amber-600/70' : 'text-neutral-800'}`}
                >
                  {p.name}
                  {p.unverified && <VerifiedBadge verified={false} lang={lang} inline />}
                </a>
              </span>
            ))}
          </span>
        )}
      </div>

      {/* Source examples (collapsed by default) */}
      {theme.sourceExamples.length > 0 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-neutral-500 hover:text-neutral-700 transition-colors mb-2"
        >
          {t('sourceExamples', lang)} {expanded ? '▲' : '▼'}
        </button>
      )}

      {expanded && theme.sourceExamples.length > 0 && (
        <ul className="space-y-1 mt-1">
          {theme.sourceExamples.map((ex, i) => {
            const url = extractUrl(ex);
            const label = url ? stripUrl(ex, url) || url : ex;
            // When a source example carries a real URL we link straight to it.
            // Older snapshots stored source examples without URLs; rather than
            // leaving a dead string (which forced the reader to copy the headline and
            // Google it by hand), we link to a web search for that exact text.
            // This invents no data — it is a lookup shortcut over the shown text.
            const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(ex)}`;
            return (
              <li key={i} className="text-xs text-neutral-500 pl-3 border-l border-neutral-300 leading-snug">
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-700 hover:text-emerald-600 underline decoration-dotted decoration-neutral-700"
                  >
                    {label}
                  </a>
                ) : (
                  <a
                    href={searchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-600 hover:text-emerald-600 underline decoration-dotted decoration-neutral-400"
                    title={t('searchThisSource', lang)}
                  >
                    {ex}
                    <span className="ml-1 text-neutral-400">↗</span>
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
